import React from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash-es';
import { FormSettingsEnum } from '@dev/ee-api/ee-api-models';
import { useDispatch, useSelector } from 'react-redux';
import { BasicInput, MultiAutocomplete, MultiAutocompleteOptionProps, SimpleToggle, Typography } from "@dev/ee-components"
import { getPaidNewsletterCreatorStore, TemplateFormPayloadUpdate, TemplateFormsCheckInvalid, TemplateFormSetMailingPreferencesLabel, TemplateIncrementAutosaveCounter } from '@/store/paidNewsletterCreator/actions';
import { FormMultiSettingsAudienceWrapper, LabelInputWrapper, MailingPreferencesWrapper, ToggleLabelWrapper, ToggleWrapper } from "./styles"
import { FormMultiSettingsAudienceProps } from './types';
import { useContactAudienceLists } from '../../hooks/useContactAudienceLists';

export const FormMultiSettingsAudience = ({
    blockClientId,
    selectedBlockClientIdRef
}: FormMultiSettingsAudienceProps): JSX.Element => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const contactLists = useContactAudienceLists();

    const { template } = useSelector(getPaidNewsletterCreatorStore);

    const clientId = blockClientId ?? selectedBlockClientIdRef.current;

    const templateForm = template.forms?.find((templateForm) => templateForm.blockClientId === clientId);

    const formPayload = templateForm?.formPayload;

    const selectedLists = formPayload?.Audience ?? [];
    const isFormAudienceError = templateForm?.isError && !formPayload?.Audience?.length;
    
    const isMailingPreferencesEnabled = formPayload?.FormSettings?.includes(FormSettingsEnum.MailingPreference) ?? false;
    const mailingPreferencesLabel =  templateForm?.formMailingPreferencesLabel ?? t('selectyoursubscriptions');

    const toggleMailingPreferences = (isEnabled: boolean): void => {
        dispatch(TemplateFormPayloadUpdate( clientId, { 
            FormSettings: 
                isEnabled && !isMailingPreferencesEnabled ? 
                [...(formPayload?.FormSettings ?? []), FormSettingsEnum.MailingPreference] :
                formPayload?.FormSettings?.filter((item) => item !== FormSettingsEnum.MailingPreference)
            } ));
        dispatch(TemplateIncrementAutosaveCounter());
    };

    const mailingPreferencesToggleConfig = {
        value: isMailingPreferencesEnabled,
        checked: isMailingPreferencesEnabled,
        label: (
            <ToggleLabelWrapper>
                <Typography variant="h4" style={{ paddingBottom: 4 }}>{t('mailingpreferences')}</Typography>
                <Typography variant="body1">{t('mailingpreferencesdescription')}</Typography>
            </ToggleLabelWrapper>
        ),
        onChange: toggleMailingPreferences
    };

    const handleOnChange = (selectedOptions: MultiAutocompleteOptionProps[]): void => {
        const chosenOptionsIds = selectedOptions?.map(option => option.value as string) ?? [];

        dispatch(TemplateFormPayloadUpdate( clientId, { Audience: chosenOptionsIds } ));
        dispatch(TemplateFormsCheckInvalid());
        if (chosenOptionsIds.length) {
            dispatch(TemplateIncrementAutosaveCounter());
        }
    };

    const getOptionsWithoutSelected = (options: MultiAutocompleteOptionProps[]): MultiAutocompleteOptionProps[] => {
        if (isEmpty(selectedLists)) {
            return options ?? [];
        }
        return options?.filter(item => !selectedLists.find(optionId => contactLists.find(list => list.value === optionId)?.label === item.label)) ?? [];
    };
    
    const handleChangeLabel = (event: React.ChangeEvent<HTMLInputElement>): void => {
        dispatch(TemplateFormSetMailingPreferencesLabel(clientId, event.target.value));
        dispatch(TemplateIncrementAutosaveCounter());
    };

    const autocompleteChosenOptions = contactLists?.filter((list) => selectedLists.includes(list.value as string)) ?? [];
    
    return (
        <>
            <FormMultiSettingsAudienceWrapper>
                <Typography variant="h3">{t('subscriberlists')}</Typography>
                <Typography variant="body1" style={{ marginTop: 8, marginBottom: 16 }}>{t('choosewherecontactdatastored')}</Typography>
                <MultiAutocomplete
                    id='select-list'
                    values={autocompleteChosenOptions}
                    options={getOptionsWithoutSelected(contactLists)}
                    onChange={handleOnChange}
                    noOptionsText={t('nolistsinfo')}
                    placeholder={t('searchorselectfromlist')}
                    error={isFormAudienceError && isEmpty(selectedLists)}
                    errorMessage={t('selectoneormorelists')}
                    inputProps={{
                        autoFocus: isFormAudienceError,
                    }}
                    labelOnly
                    hideGroupLabel
                />
            </FormMultiSettingsAudienceWrapper>
            <MailingPreferencesWrapper>
                <ToggleWrapper>
                    <SimpleToggle {...mailingPreferencesToggleConfig} />
                </ToggleWrapper>
                {isMailingPreferencesEnabled && (
                    <LabelInputWrapper>
                        <BasicInput value={mailingPreferencesLabel} label={t('label')} onChange={handleChangeLabel} />
                    </LabelInputWrapper>
                )}
            </MailingPreferencesWrapper>
        </>
    );
}